/** Shared display helpers plus (now empty) legacy showcase collections. */

export interface ShowcasePerson {
  id: string;
  name: string;
  handle: string;
  hue: number;
  city: string;
  bio: string;
  verified: boolean;
  followers: number;
  rating: number;
  tags: string[];
}

export interface ShowcasePost {
  id: string;
  author: ShowcasePerson;
  content: string;
  kind: string;
  tags: string[];
  likes: number;
  hoursAgo: number;
  hasMedia: boolean;
}

export interface ShowcaseEvent {
  id: string;
  title: string;
  host: ShowcasePerson;
  venue: string;
  city: string;
  inDays: number;
  genres: string[];
  priceFrom: number;
  capacity: number;
}

export interface ShowcaseProduct {
  id: string;
  title: string;
  description: string;
  category: "merch" | "music" | "subscription";
  price: number;
  seller: ShowcasePerson;
}

export interface ShowcaseTrack {
  id: string;
  title: string;
  artist: ShowcasePerson;
  genre: string;
  bpm: number;
  duration: string;
  price: number;
}

export interface ShowcaseStream {
  id: string;
  title: string;
  host: ShowcasePerson;
  city: string;
  viewers: number;
}

export interface ShowcaseComment {
  id: string;
  author: ShowcasePerson;
  body: string;
  createdAt: Date;
}

/**
 * Mock/demo content has been removed — the platform now shows only real records.
 * These exports remain (empty) so discovery surfaces keep their empty-state handling.
 */
export const SHOWCASE_CREATORS: ShowcasePerson[] = [];
export const SHOWCASE_VENUES: ShowcasePerson[] = [];
export const SHOWCASE_POSTS: ShowcasePost[] = [];
export const SHOWCASE_EVENTS: ShowcaseEvent[] = [];
export const SHOWCASE_PRODUCTS: ShowcaseProduct[] = [];
export const SHOWCASE_TRACKS: ShowcaseTrack[] = [];
export const SHOWCASE_STREAMS: ShowcaseStream[] = [];

/**
 * Sample comments for showcase reels (to demonstrate UI when no real data exists)
 */
export const SHOWCASE_COMMENTS: Record<string, ShowcaseComment[]> = {
  "sample-1": [
    {
      id: "c1",
      author: {
        id: "u1",
        name: "HouseHead_ZA",
        handle: "househead_za",
        hue: 280,
        city: "Cape Town",
        bio: "Deep house enthusiast",
        verified: true,
        followers: 12500,
        rating: 4.8,
        tags: ["house", "techno"]
      },
      body: "This track is absolutely fire! 🔥 Can't wait for the full release",
      createdAt: new Date(Date.now() - 1000 * 60 * 5) // 5 minutes ago
    },
    {
      id: "c2",
      author: {
        id: "u2",
        name: "MusicLover_SA",
        handle: "musiclover_sa",
        hue: 120,
        city: "Johannesburg",
        bio: "Amapiano & Afrohouse fan",
        verified: false,
        followers: 3500,
        rating: 4.5,
        tags: ["afrohouse", "amapiano"]
      },
      body: "Metro Beats never disappoints! 🎵",
      createdAt: new Date(Date.now() - 1000 * 60 * 15) // 15 minutes ago
    },
    {
      id: "c3",
      author: {
        id: "u3",
        name: "ClubVibes",
        handle: "clubvibes",
        hue: 200,
        city: "Durban",
        bio: "Weekend warrior",
        verified: false,
        followers: 2100,
        rating: 4.3,
        tags: ["house", "tech"]
      },
      body: "This needs to be played at every club this weekend!",
      createdAt: new Date(Date.now() - 1000 * 60 * 45) // 45 minutes ago
    }
  ],
  "sample-2": [
    {
      id: "c4",
      author: {
        id: "u4",
        name: "BassMaster",
        handle: "bassmaster",
        hue: 30,
        city: "Durban",
        bio: "Deep house fanatic",
        verified: true,
        followers: 8900,
        rating: 4.7,
        tags: ["deephouse", "tech"]
      },
      body: "The energy in this set is insane! �",
      createdAt: new Date(Date.now() - 1000 * 60 * 30) // 30 minutes ago
    },
    {
      id: "c5",
      author: {
        id: "u5",
        name: "PartyGoer",
        handle: "partygoer",
        hue: 60,
        city: "Pretoria",
        bio: "Living for the weekend",
        verified: false,
        followers: 1800,
        rating: 4.2,
        tags: ["party", "house"]
      },
      body: "DJ Nova bringing the heat as always! 🔥",
      createdAt: new Date(Date.now() - 1000 * 60 * 60) // 1 hour ago
    }
  ]
};

/**
 * Get sample comments for a showcase reel
 */
export const getShowcaseComments = (reelId: string): ShowcaseComment[] => {
  return SHOWCASE_COMMENTS[reelId] || [];
};

/** Deterministic hue for any id so real records without artwork still look designed. */
export const hueFromId = (id: string) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 360;
  return h;
};

export const artGradient = (hue: number) =>
  `linear-gradient(135deg, hsl(${hue} 72% 42%) 0%, hsl(${(hue + 45) % 360} 65% 22%) 55%, hsl(${(hue + 90) % 360} 60% 12%) 100%)`;

export const compactNumber = (n: number) =>
  n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K` : `${n}`;
